import React, { Suspense, Component, useEffect, useContext } from 'react';
import { Route, Redirect, Switch, Link, useParams, withRouter } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';

import * as Sentry from '@sentry/react';

import { map, get } from 'lodash';

import { isSuperAdmin, isAdmin, isAuthenticated, isContentManager, isRetail, isBasic } from './utils/acls';

import { ObjectiveProvider } from './context/objective';
import Loading from 'components/v2/Loading';
import { ReactComponent as ItrvlLogo } from 'common/assets/itrvl_logo--white.svg';
import { ReactComponent as BrokenVehicle } from 'common/assets/brokenVehicle.svg';

import withUser from 'common/hocs/withUser';
import lazy from 'common/utils/lazy';
import { useApi } from 'common/hooks/api';
import { UserContext } from 'common/context';
import { AUDIT_TRAIL_ACTIONS, FEATURE_FLAG } from 'itrvl-types';
import usePageTracking from 'hooks/usePageTracking';
import { useCustomizations } from 'context/ThemeContext';
//TODO - either make lazy or move into routes for the ContentManager
import logger from 'itrvl-logger';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const MyProfile = lazy(() => import('pages/MyProfile'), { chunkName: 'MyProfile' });

const log = logger(__filename);

const Config = lazy(() => import('common/components/Config'), {
  chunkName: 'Config',
});

const Clients = lazy(() => import('./pages/Clients'), {
  chunkName: 'Clients',
});

const ContentManager = lazy(() => import('./pages/ContentManager'), { chunkName: 'ContentManager' });
const AccommodationsEditor = lazy(() => import('./pages/ContentManager/AccommodationsManager/AccommodationsEditor'));
const ServicesEditor = lazy(() => import('./pages/ContentManager/ServicesManager/ServicesEditor'));
const CountriesEditor = lazy(() => import('./pages/ContentManager/CountriesManager/CountriesEditor'));
const RegionsEditor = lazy(() => import('./pages/ContentManager/RegionsManager/RegionsEditor'));
const RoomsEditor = lazy(() => import('./pages/ContentManager/RoomsManager/RoomsEditor'));

const MyAgents = lazy(() => import('./pages/MyAgents'), {
  chunkName: 'MyAgents',
});
const Agencies = lazy(() => import('./pages/Agencies/Agencies'), {
  chunkName: 'Agencies',
});
const AgencyDetails = lazy(() => import('./pages/Agencies/AgencyDetails'), {
  chunkName: 'AgencyDetails',
});
const MyAgency = lazy(() => import('./pages/MyAgency'), { chunkName: 'MyAgency' });
const ClientDetails = lazy(() => import('./pages/Clients/ClientDetails'), {
  chunkName: 'ClientDetails',
});
const ItrvlAppBar = lazy(() => import('./components/ItrvlAppBar'), {
  chunkName: 'ItrvlAppBar',
});
const StatusBar = lazy(() => import('./components/StatusBar'), {
  chunkName: 'StatusBar',
});

const ContentUploader = lazy(() => import('./pages/ContentLibrary/ContentUploader'), { chunkName: 'ContentUploader' });
const ContentLibraryEntry = lazy(() => import('./pages/ContentLibrary/ContentLibraryEntry'), { chunkName: 'ContentLibrary' });

const MasqueradeEnded = lazy(() => import('./pages/Agencies/MasqueradeEnded'), {
  chunkName: 'MasqueradeEnded',
});
const WelcomeWizard = lazy(() => import('./pages/WelcomeWizard'), {
  chunkName: 'WelcomeWizard',
});
const LoginPage = lazy(() => import('./pages/Login'), {
  chunkName: 'Login',
});
const Dashboard = lazy(() => import('./pages/Dashboard'), {
  chunkName: 'Dashboard',
});
const Notifications = lazy(() => import('./pages/Notifications'), {
  chunkName: 'Notifications',
});
const Chat = lazy(() => import('./pages/Chat'), {
  chunkName: 'Chat',
});
const EdgeCatalog = lazy(() => import('./pages/Edge/Catalog'), {
  chunkName: 'EdgeCatalog',
});

const ClusterExplorer = lazy(() => import('./pages/Insights/ClusterExplorer'), { chunkName: 'ClusterExplorer' });
const ShapeMap = lazy(() => import('./pages/Insights/ShapeMap'), { chunkName: 'ShapeMap' });

// v2
const ItineraryBuilderEntry = lazy(() => import('./pages/ItineraryBuilder/ItineraryBuilderEntry'), { chunkName: 'ItineraryBuilder' });
const EditItineraryEntry = lazy(() => import('./pages/EditItinerary/EditItineraryEntry'), { chunkName: 'EditItinerary' });
const PresentationEdit = lazy(() => import('./pages/PresentationEdit'), { chunkName: 'PresentationEdit' });
const EdgeSuccess = lazy(() => import('./pages/Edge/Success'), { chunkName: 'EdgeSuccess' });
const EdgeDetail = lazy(() => import('./pages/Edge/Detail'), { chunkName: 'EdgeDetail' });

const SentryRoute = Sentry.withSentryRouting(Route);
const notFoundStyle = makeStyles(
  {
    main: {
      color: 'white',
      backgroundColor: 'black',
      width: '100%',
      height: '100%',
      flex: 1,
    },
    header: {
      height: '69px',
    },
    blueBackground: {
      backgroundColor: '#639fc3',
    },
    body: {
      borderRadius: '5px',
      width: '80%',
      margin: '25% auto',
      padding: '10px',
      '& > div': {
        margin: '10px auto',
        textAlign: 'center',
      },
    },
    p1: {
      fontSize: '48px',
    },
    p2: {
      fontSize: '24px',
    },
    logo: {
      margin: '15px',
      height: '100px',
    },
  },
  { name: `${__filename}.notFoundStyle` },
);

const NotFound = () => {
  const classes = notFoundStyle();

  return (
    <div className={classes.main}>
      <div className={classes.header}>
        <ItrvlLogo className={classes.logo} alt="itrvl Logo" />
      </div>
      <div className={clsx(classes.body, classes.blueBackground)}>
        <div>
          <BrokenVehicle alt="Broken Vehicle" />
        </div>
        <div className={classes.p1}>Oops! Something went wrong.</div>
        <div className={classes.p2}>
          You can try refreshing the page, go back, or <Link to="/">go home</Link>.
        </div>
      </div>
    </div>
  );
};

const TestRoute = ({ component: Component, ...rest }) => <SentryRoute {...rest} component={Component} />;
const SentryError = props => {
  const pretag = props.history.action === 'PUSH' ? '' : 'web-';
  return (
    <SentryRoute
      {...props}
      component={() => {
        throw new Error(`throw.sentry.${pretag}agent`);
      }}
    />
  );
};

const Content = ({ children }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.warm,
      }}
    >
      {children}
    </div>
  );
};

const useStyles = makeStyles(
  theme => ({
    root: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    '@global': {
      a: {
        color: theme.palette.secondary.main,
        textDecoration: 'none',
        transition: theme.transitions.create(['color']),
        '&:hover': {
          color: theme.palette.secondary.dark,
        },
      },
    },
  }),
  { name: 'routes' },
);

const ProtectedContent = ({ children }) => {
  const classes = useStyles();
  return (
    <div className={classes.root} data-scroll-container>
      {children}
    </div>
  );
};

const AuditTrail = ({ logAudit, ...props }) => {
  const params = useParams();
  const { user } = useContext(UserContext);
  const Api = useApi();

  useEffect(() => {
    const sendLogAudit = async () => {
      const values = {
        params,
        user,
      };
      const { action = '', matchProps = {} } = logAudit ?? {};
      const idProps = Object.entries(matchProps).reduce((res, [key, value]) => {
        res[key] = get(values, value);
        return res;
      }, {});

      try {
        const { data } = await Api.logAudit({
          action,
          ...idProps,
        });
        log.debug(data);
      } catch (err) {
        log.error(err);
      }
    };

    if (logAudit) {
      try {
        sendLogAudit();
      } catch (err) {
        log.error(err);
      }
    }
  }, [Api, logAudit, params, user]);

  return <>{props.children}</>;
};

const ProtectedRoute = props => {
  const {
    logAudit,
    useFixedAppbar = false,
    hideFooter = false,
    showObjectives = false,
    isAccessAllowed,
    component: Component,
    ...rest
  } = props;

  usePageTracking(props);

  useEffect(() => {
    document.title = props.name;
  }, [props.name]);

  return (
    <SentryRoute
      {...rest}
      render={props =>
        isAccessAllowed ? (
          <React.Fragment>
            <AuditTrail logAudit={logAudit}>
              <ItrvlAppBar useFixedAppbar={useFixedAppbar} />
              <ProtectedContent>
                <Suspense fallback={<Loading />}>
                  <Component {...props} />
                </Suspense>
              </ProtectedContent>
              {showObjectives && (
                <ObjectiveProvider>
                  <WelcomeWizard />
                </ObjectiveProvider>
              )}
              {hideFooter !== true && <StatusBar />}
            </AuditTrail>
          </React.Fragment>
        ) : (
          <Redirect
            to={{
              pathname: props.location?.pathname?.startsWith('/edge/') ? '/login' : '/',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

const BareProtectedRoute = props => {
  const { logAudit, showObjectives = false, isAccessAllowed, component: Component, ...rest } = props;
  log.debug('Bare Protected Route:', Component.name);

  return (
    <SentryRoute
      {...rest}
      render={props =>
        isAccessAllowed ? (
          <React.Fragment>
            <AuditTrail logAudit={logAudit}>
              <Suspense fallback={<Loading />}>
                <Component {...props} />
              </Suspense>
              {showObjectives && (
                <ObjectiveProvider>
                  <WelcomeWizard />
                </ObjectiveProvider>
              )}
            </AuditTrail>
          </React.Fragment>
        ) : (
          <Redirect
            to={{
              pathname: props.location?.pathname?.startsWith('/edge/') ? '/login' : '/',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

const AdminRoute = ({ userContext: { user }, ...rest }) => {
  log.trace('AdminRoute');
  return <ProtectedRoute user={user} isAccessAllowed={isAdmin(user)} showObjectives {...rest} />;
};
const ContentManagerRoute = ({ userContext: { user }, ...rest }) => {
  log.trace('ContentManagerRoute');
  return <ProtectedRoute isAccessAllowed={isContentManager(user)} showObjectives={false} {...rest} />;
};
const RetailRoute = ({ userContext: { user }, ...rest }) => {
  log.trace('RetailRoute');
  return <ProtectedRoute isAccessAllowed={isRetail(user)} showObjectives={false} {...rest} />;
};
const BasicRoute = ({ userContext: { user }, ...rest }) => {
  log.trace('BasicRoute');
  return <ProtectedRoute isAccessAllowed={isBasic(user)} showObjectives {...rest} />;
};

const SuperAdminRoute = ({ userContext: { user }, ...rest }) => {
  log.trace('SuperAdminRoute');
  return <ProtectedRoute isAccessAllowed={isSuperAdmin(user)} showObjectives={false} {...rest} />;
};

const AuthenticatedRoute = ({ userContext: { user }, useBarePage, requiresFeatureFlag, ...rest }) => {
  log.trace('AuthenticatedRoute');
  const customizations = useCustomizations();

  // Check if the route requires a feature flag
  let isAccessAllowed = isAuthenticated(user);
  if (isAccessAllowed && requiresFeatureFlag) {
    const hasFeatureFlag = customizations?.[requiresFeatureFlag] === true;
    isAccessAllowed = hasFeatureFlag;
  }

  if (useBarePage) return <BareProtectedRoute isAccessAllowed={isAccessAllowed} showObjectives {...rest} />;
  return <ProtectedRoute isAccessAllowed={isAccessAllowed} showObjectives {...rest} />;
};

const NotAuthenticatedOrRedirect = ({ component: Component, authenticatedRedirect, userContext: { user }, ...rest }) => {
  log.debug('NotAuthentitedOrRedirect', Component.name);
  const redirect = isRetail(user) && !isSuperAdmin(user) ? '/edge' : authenticatedRedirect;

  return (
    <SentryRoute
      {...rest}
      render={props =>
        isAuthenticated(user) ? (
          <Redirect
            to={{
              pathname: redirect,
              state: { from: props.location },
            }}
          />
        ) : (
          <Suspense fallback={<Loading />}>
            <Component {...props} />
          </Suspense>
        )
      }
    />
  );
};

const HomeUnauthenticated = ({ location }) => <Redirect to={{ pathname: '/login', state: { from: location } }} />;

// Add flag functions for routes with menuRequiresFeatureFlag
const addMenuFeatureFlags = routes => {
  return routes.map(route => {
    if (route.menuRequiresFeatureFlag) {
      return {
        ...route,
        flag: (_user, customizations) => {
          return customizations?.[route.menuRequiresFeatureFlag] === true;
        },
      };
    }
    return route;
  });
};

// Exposed for SSR render and SiteMenu
export const routes = addMenuFeatureFlags([
  {
    name: 'sentryError',
    path: '/sentryError',
    exact: true,
    component: SentryError,
    dev: true,
  },
  {
    name: 'home',
    exact: true,
    authenticated: true,
    path: '/',
    component: HomeUnauthenticated,
    authenticatedRedirect: '/dashboard',
  },
  {
    name: 'Notifications',
    path: '/notifications',
    component: Notifications,
    exact: true,
    authenticated: true,
  },
  {
    name: 'Dashboard',
    exact: true,
    authenticated: true,
    path: '/dashboard',
    component: Dashboard,
    menu: true,
    icon: 'RefNumber',
    basic: true,
  },
  {
    name: 'Chat',
    authenticated: true,
    path: '/chat',
    component: Chat,
    requiresFeatureFlag: FEATURE_FLAG.ALLOW_CHAT,
    menu: true,
    menuRequiresFeatureFlag: FEATURE_FLAG.ALLOW_CHAT,
    Icon: ChatBubbleOutlineIcon,
    experimental: true,
    basic: true,
  },
  {
    name: 'My Profile',
    exact: true,
    authenticated: true,
    path: '/my-profile',
    component: MyProfile,
    menu: true,
    icon: 'myProfile',
    retail: true,
  },
  {
    name: 'Edge Catalog',
    exact: true,
    authenticated: true,
    path: '/edge',
    component: EdgeCatalog,
    retail: true,
    menu: true,
    icon: 'itinerary',
  },
  {
    name: 'Edge Success',
    exact: true,
    authenticated: true,
    path: '/edge/:id/success/:successId',
    component: EdgeSuccess,
    retail: true,
  },
  {
    name: 'Edge Detail',
    exact: true,
    authenticated: true,
    path: '/edge/:id',
    component: EdgeDetail,
    retail: true,
  },
  {
    name: 'My Profile',
    exact: true,
    authenticated: true,
    path: '/my-profile',
    component: MyProfile,
    menu: true,
    icon: 'myProfile',
    basic: true,
  },
  {
    name: 'Agents Profile',
    exact: true,
    authenticated: true,
    path: '/agents/:id',
    component: MyProfile,
    menu: false,
  },
  {
    name: 'verify',
    path: '/:route(verify)/:uid/:token',
    component: LoginPage,
  },
  {
    name: 'signup',
    exact: true,
    path: '/signup/:agencyId?',
    component: LoginPage,
  },
  {
    name: 'login',
    path: '/:route(login|forgot|forgotten|loggedOut|reset|register|registered|verified|verify)/:token?',
    component: LoginPage,
  },
  {
    name: 'Content Library',
    exact: true,
    path: '/content',
    component: ContentLibraryEntry,
    contentmanager: true,
    menu: true,
    icon: 'categories',
  },
  {
    name: 'uploads',
    exact: true,
    authenticated: true,
    path: '/content/uploads',
    component: ContentUploader,
  },
  {
    name: 'Itinerary Builder',
    exact: true,
    authenticated: true,
    path: '/itinerary-builder',
    menu: true,
    icon: 'planner',
    component: ItineraryBuilderEntry,
    basic: true,
  },
  {
    name: 'Edge Catalog',
    exact: true,
    authenticated: true,
    path: '/edge',
    component: EdgeCatalog,
    basic: true,
    menu: true,
    icon: 'itinerary',
  },
  {
    name: 'Edge Success',
    exact: true,
    authenticated: true,
    path: '/edge/:id/success/:successId',
    component: EdgeSuccess,
    basic: true,
  },
  {
    name: 'Edge Detail',
    exact: true,
    authenticated: true,
    path: '/edge/:id',
    component: EdgeDetail,
    basic: true,
  },
  {
    name: 'Itinerary',
    exact: true,
    authenticated: true,
    path: '/itinerary/:id',
    component: EditItineraryEntry,
  },
  {
    name: 'Itinerary',
    exact: true,
    authenticated: true,
    path: '/itinerary/:id/:edit(edit-dmc|edit-transit|edit-services)',
    component: EditItineraryEntry,
  },
  {
    name: 'PresentationRedirect',
    exact: true,
    relative: true,
    from: '/itinerary/:id/presentation',
    to: '/welcome',
    redirect: true,
  },
  {
    name: 'Presentation Editor',
    path: '/itinerary/:id/presentation',
    authenticated: true,
    useFixedAppbar: true,
    hideFooter: true,
    component: PresentationEdit,
  },
  {
    name: 'My Agents',
    exact: true,
    path: '/agents',
    component: MyAgents,
    admin: true,
    menu: true,
    icon: 'onRequest',
  },
  {
    name: 'My Agency',
    exact: true,
    path: '/my-agency',
    component: MyAgency,
    admin: true,
    menu: true,
    icon: 'settings',
  },
  {
    name: 'Agency',
    exact: true,
    superadmin: true,
    path: '/agencies/:id',
    component: AgencyDetails,
  },
  {
    name: 'All Agencies',
    exact: true,
    superadmin: true,
    path: '/agencies',
    component: Agencies,
    menu: true,
    icon: 'edit',
  },
  {
    name: 'My Clients',
    exact: true,
    authenticated: true,
    path: '/clients',
    component: Clients,
    menu: true,
    icon: 'profile',
    basic: true,
  },
  {
    name: 'Agent Clients',
    exact: true,
    authenticated: true,
    path: '/agents/:agentId/clients',
    component: Clients,
  },
  {
    name: 'Client',
    exact: true,
    authenticated: true,
    path: '/clients/:id',
    component: ClientDetails,
    logAudit: {
      action: AUDIT_TRAIL_ACTIONS.AGENT_ACCESSED_CLIENT,
      matchProps: {
        clientId: 'params.id',
        agentId: 'user.userId',
      },
    },
    menu: false,
  },
  {
    name: 'masquerade-end',
    path: '/masquerade-end',
    exact: true,
    component: MasqueradeEnded,
    authenticated: true,
  },
  {
    name: 'Content Manager',
    path: '/content-manager',
    exact: true,
    component: ContentManager,
    contentmanager: true,
    menu: true,
    icon: 'camp',
  },
  {
    name: 'Content Manager',
    path: '/content-manager/:tab(accommodations|services|countries|regions|rooms)?',
    exact: true,
    component: ContentManager,
    contentmanager: true,
  },
  {
    name: 'Accommodations Manager',
    path: '/content-manager/accommodations/:id/:tab(details|content|video|debug|extras)?',
    exact: true,
    component: AccommodationsEditor,
    contentmanager: true,
  },
  {
    name: 'Services Manager',
    path: '/content-manager/services/:id/:tab(details|content|debug)?',
    exact: true,
    component: ServicesEditor,
    contentmanager: true,
  },
  {
    name: 'Countries Manager',
    path: '/content-manager/countries/:id/:tab(details|content|debug)?',
    exact: true,
    component: CountriesEditor,
    contentmanager: true,
  },
  {
    name: 'Regions Manager',
    path: '/content-manager/regions/:id/:tab(details|content|debug)?',
    exact: true,
    component: RegionsEditor,
    contentmanager: true,
  },
  {
    name: 'Rooms Manager',
    path: '/content-manager/rooms/:id/:tab(details|content)?',
    exact: true,
    component: RoomsEditor,
    contentmanager: true,
  },
  {
    name: 'config',
    path: '/config',
    exact: true,
    component: Config,
    superadmin: true,
  },
  {
    name: 'Insights',
    exact: true,
    path: '/insights/clusters',
    component: ClusterExplorer,
    menu: true,
    icon: 'analytics',
    superadmin: true,
    hideFooter: true,
  },
  {
    name: 'Shape Explorer',
    exact: true,
    path: '/insights/shapes/map',
    component: ShapeMap,
    menu: false,
    superadmin: true,
    hideFooter: true,
  },
  // This should always be last
  {
    name: 'NotFound',
    path: '*',
    component: NotFound,
  },
]);

/*
else if (route.supercontentmanager) {
  ret = <SuperContentManagerRoute key={route.name} {...props} {...route} />;
}
* */
function buildRoute(route, props) {
  let ret;
  if (route.test) {
    return <TestRoute key={route.name} {...props} {...route} />;
  }
  if (route.admin) {
    ret = <AdminRoute key={route.name} {...props} {...route} />;
  } else if (route.contentmanager) {
    ret = <ContentManagerRoute key={route.name} {...props} {...route} />;
  } else if (route.retail) {
    if (!isRetail(get(props, 'userContext.user')) && isAuthenticated(get(props, 'userContext.user'))) {
      return null;
    }
    ret = <RetailRoute key={route.name} {...props} {...route} />;
  } else if (route.basic) {
    ret = <BasicRoute key={route.name} {...props} {...route} />;
  } else if (route.superadmin) {
    ret = <SuperAdminRoute key={route.name} {...props} {...route} />;
  } else if (route.authenticated) {
    ret = route.authenticatedRedirect ? (
      <NotAuthenticatedOrRedirect key={route.name} {...props} {...route} />
    ) : (
      <AuthenticatedRoute key={route.name} {...props} {...route} />
    );
  } else if (route.authenticated && route.authenticatedComponent) {
    ret = <AuthenticatedRoute key={route.name} {...props} {...route} />;
  } else if (route.redirect) {
    const { pathname } = props.location;
    const pathWithoutTrailingSlash = pathname[pathname.length - 1] === '/' ? pathname.slice(0, -1) : pathname;
    ret = (
      <Redirect
        key={route.name}
        {...props}
        exact={!!route.exact}
        from="/itinerary/:id/presentation"
        to={{
          pathname: route.relative ? `${pathWithoutTrailingSlash}${route.to}` : route.to,
          state: { from: props.location },
        }}
      />
    );
  } else {
    ret = <SentryRoute key={route.name} {...props} {...route} />;
  }
  return ret;
}

class Routes extends Component {
  render() {
    log.debug('Routes');
    return (
      <Content>
        <Suspense fallback={<Loading />}>
          <Switch>{map(routes, route => buildRoute(route, this.props))}</Switch>
        </Suspense>
      </Content>
    );
  }
}

export default withRouter(withUser(Routes));
